/* tslint:disable:no-empty-interface */
import { RouteComponentProps } from 'react-router-dom';
import { ClientName, ClientShortHandName } from '@snoam/mono-scc';
import { apiStatuses, IExceptionAction } from './actions/Actions';

export type ProductId = number | string;

export interface IApiState {
  apiStatus?: apiStatuses;
}
export interface IValidLPCode extends IApiState {
  valid: boolean;
}
export interface ICardDetails {
  expiryDate?: IDate;
  maskedNumber?: string;
  panId?: string;
  paymentMethod?: string;
  phoneNumber?: string;
  threeDSecure?: boolean;
}
export interface IDate {
  year: number;
  month: number;
  day: number;
}
export interface IDateTime {
  dateTime: {
    date: {
      year: number;
      month: number;
      day: number;
    };
    time: {
      hour: number;
      minute: number;
      second: number;
      nano: number;
    };
  };
  offset: {
    totalSeconds: number;
  };
}
export enum PaymentMethod {
  PAYEX = "PAYEX",
  STREX = "STREX",
  VIPPS = "VIPPS",
  VIPPS_RECURRING = "VIPPS_RECURRING",
}
export interface IPaymentDetails {
  created?: IDateTime;
  details?: ICardDetails;
  id?: number;
  maxAmount?: number;
  status?: string;
  type?: string;
  updated?: IDateTime;
  userId?: number;
}
export interface ICheckCode extends IApiState {
  firstName: string;
  lastName: string;
  spidId: number;
  expiresIn: number;
  code: string;
  exception?: 'true';
  paymentDetails: IPaymentDetails[];
  sig: string;
}
export interface ICreateOrder extends IApiState {
  loadingText: string;
}
export interface IProductInfo extends IApiState {
  allowedPaymentMethods: PaymentMethod[];
  customerserviceEmail?: string | '';
  cancellationInfo?: string;
  header?: string;
  goDirectlyToReceiptPage?: boolean;
  imageurl?: string;
  backgroundImageUrl?: string;
  myPageUrl?: string;
  orderButtonText?: string;
  owningClient?: ClientShortHandName | '';
  owningClientName?: ClientName | '';
  paymentMethodCompanyPays?: boolean;
  paymentMethodFree?: boolean;
  paymentMethodVipps?: boolean;
  phoneNumber?: string;
  price?: number;
  productId?: string;
  productMappingId?: number;
  pureDigital?: boolean;
  questionAndAnswersUrl?: string;
  subscriptionConfigurationId?: number;
  subscriptionTypeId?: number;
  text1?: string;
  text2?: string;
  text3?: string;
  hideFromWebSearch?: null | boolean;
  productCampaign?: string;
  productType?: string;
  rckEnableFirstservice?: boolean;
  rckEnableOnsite?: boolean;
  rckPathId?: string;
  receiptPageCenter?: string;
  receiptPageBottom?: string;
  receiptPageAboveButton?: string;
  receiptPageButton?: string;
  receiptPageNotLoggedInButton?: string;
  receiptPageButtonUrl?: string;
  recurringInfo?: string;
  showFamilySharing?: boolean;
  spidTeaser?: string;
  subHeader?: string;
  upsaleHeading?: string;
  upsaleSubscriptionConfigurationId?: number;
  validZipCodes?: number[];
  loyaltyPartner?: ILoyaltyPartner;
  onlyForCertainAge?: boolean;
  ageLimit?: number;
  ageLimitSubscriptionConfigurationId?: number;
  extraAllowedRedirectDomains?: string[];
}
export interface ILoyaltyPartner {
  name: string;
  cardNumberPrefix?: string;
  minLength?: number;
  maxLength?: number;
  placeholder: string;
  validator: string;
}
export interface ISessionInfo extends IApiState {
  result?: boolean;
  serverTime?: number;
  expiresIn?: number;
  baseDomain?: string;
  visitor?: {
    uid: string;
    user_id: string;
  };
  id?: string;
  userId?: number;
  displayName?: string;
  givenName?: string;
  familyName?: string;
  gender?: string;
  photo?: string;
  tracking?: boolean;
  userStatus?: string;
  clientAgreementAccepted?: boolean;
  defaultAgreementAccepted?: boolean;
  sp_id?: string;
  sig?: string;
}
export interface IUserInfo extends IApiState {
  birthDay?: string;
  siebelUserExists?: boolean;
  siebelBirthDate?: string;
  birthMonth?: string;
  birthYear?: string;
  cellphone?: string;
  city?: string;
  expiresIn?: number;
  email?: string;
  firstName?: string;
  hasProduct?: boolean;
  lastName?: string;
  loyaltyChecked?: boolean;
  mobileNumber?: string;
  name?: string;
  paymentDetails?: IPaymentDetails[];
  paymentMethods?: Array<{
    id: number;
  }>;
  phoneNumber?: string;
  serverTime?: number;
  sig?: string;
  sigFromCode?: boolean;
  spidId?: number;
  spidPrimaryEmail?: string;
  validUntil?: number;
  zipCode?: string;
  acceptedAgreement?: boolean;
  loyaltyCode?: string;
  valid?: boolean;
  vippsEnabled?: boolean;
}
export interface IUserLoggedIn {
  sig?: string;
  spidId?: string;
}
export interface IDuplicateAndQuarantine {
  isDuplicate?: boolean;
  isQuarantine?: boolean;
  shouldUpgrade?: boolean;
  quarantineText?: string;
  quarantineUrl?: string;
  isQuarantineFullPrice?: boolean;
}
export interface ITimeout {
  showWarning?: boolean;
}
export interface IAddress {
  apartmentNumber: string;
  city: string;
  deliveryPointId: string;
  entrance: string;
  floor: string;
  houseNumber: string;
  coAddress: string;
  streetAddress: string;
  zipCode: string;
}
export interface ICode {
  code?: string;
  valid?: boolean;
  showValidationError?: boolean;
  sentCodeBySms?: boolean;
}
export interface IReferer {
  referer?: string;
  redirectUri?: string;
}
export interface IUtm {
  source?: string;
  medium?: string;
  campaign?: string;
  term?: string;
  content?: string;
}

export interface IOrganizationSearch {
  input: string;
  data: IOrganizationData;
}

export interface IOrganizationData {
  organisasjonsnummer: string;
  navn: string;
  organisasjonsform: {
    kode: string;
    beskrivelse: string;
  };
  postadresse?: IOrganizationAddress;
  forretningsadresse?: IOrganizationAddress;
}

export interface IOrganizationAddress {
  land: string;
  landkode: string;
  postnummer: string;
  poststed: string;
  adresse: string[];
  kommune: string;
  kommunenummer: string;
}

export interface IPayex {
  widgetUrl: string;
  loading: boolean;
}

export interface IState {
  [k: string]: any;
  address: IAddress;
  alternateOfferProductInfo?: IProductInfo;
  checkCode: ICheckCode;
  exceptions: IExceptionAction[];
  fullPriceProductInfo?: IProductInfo;
  productInfo: IProductInfo;
  validPLCode: IValidLPCode;
  referer: IReferer;
  utm: IUtm;
  router: RouteComponentProps<{
    productId: string;
  }>;
  sessionInfo: ISessionInfo;
  timeout: ITimeout;
  upsaleProductInfo: IProductInfo;
  userInfo: IUserInfo;
  checkDuplicateAndQuarantine: IDuplicateAndQuarantine;
  organizationData: IOrganizationSearch;
  payex: IPayex;
}

export interface IQuery {
  [k: string]: string;
}
